import { useId } from "react";
import type { ReactNode } from "react";
import { motion } from "motion/react";
import type { SlideProps } from "@/engine/types";
import {
  GlassCard,
  GradientText,
  RevealItem,
  RevealList,
  SlideShell,
  useAccent,
} from "@/ui";

/** Timing shared between the row reveals and the self-ticking checkboxes. */
const START = 0.2;
const STAGGER = 0.35;
const TICK_LAG = 0.4;

function Strong({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-white">{children}</strong>;
}

/* His four checklist bullets, verbatim — bolds kept as emphasis. */
const ITEMS: { key: string; text: ReactNode }[] = [
  {
    key: "title-meta",
    text: (
      <>
        <GradientText className="font-semibold">Title + meta</GradientText> unique
        per page; <Strong>H1</Strong> matches the page&rsquo;s main promise.
      </>
    ),
  },
  {
    key: "internal-links",
    text: (
      <>
        <GradientText className="font-semibold">Internal links</GradientText> from
        related pages use <Strong>helpful anchor text</Strong> (varied, not
        spammy).
      </>
    ),
  },
  {
    key: "images",
    text: (
      <>
        <GradientText className="font-semibold">Images</GradientText>: meaningful
        file names and <Strong>alt text</Strong> where they carry information;{" "}
        <Strong>fetchpriority high</Strong> for important images in the viewport.
      </>
    ),
  },
  {
    key: "core-page",
    text: (
      <>
        <GradientText className="font-semibold">Core page</GradientText> fast on
        mobile: no blocking <Strong>huge assets</Strong> in the first screen.
      </>
    ),
  },
];

/** Square checkbox that ticks itself in sync with its row's entrance. */
function AnimatedCheck({ active, delay }: { active: boolean; delay: number }) {
  const gradId = useId();
  const [from, to] = useAccent();
  return (
    <div className="relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/15 bg-white/5">
      <motion.div
        className="absolute inset-0"
        style={{ backgroundImage: `linear-gradient(135deg, ${from}2e, ${to}2e)` }}
        initial={{ opacity: 0 }}
        animate={active ? { opacity: 1 } : { opacity: 0 }}
        transition={active ? { delay, duration: 0.35 } : { duration: 0.2 }}
      />
      <svg viewBox="0 0 24 24" className="relative h-8 w-8" fill="none" aria-hidden>
        <defs>
          <linearGradient
            id={gradId}
            x1="0"
            y1="0"
            x2="24"
            y2="24"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={from} />
            <stop offset="1" stopColor={to} />
          </linearGradient>
        </defs>
        <motion.path
          d="M4.5 12.5l5 5L19.5 7"
          stroke={`url(#${gradId})`}
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={
            active ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }
          }
          transition={
            active ? { delay, duration: 0.5, ease: "easeOut" } : { duration: 0.2 }
          }
        />
      </svg>
    </div>
  );
}

export function OnPageChecklistSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={2} title="On-page checklist">
      <RevealList
        active={active}
        stagger={STAGGER}
        delay={START}
        className="mx-auto flex w-full max-w-5xl flex-col gap-6"
      >
        {ITEMS.map((item, i) => (
          <RevealItem key={item.key}>
            <GlassCard className="flex items-center gap-7 px-8 py-6 transition-colors duration-300 hover:bg-white/10">
              <AnimatedCheck active={active} delay={START + i * STAGGER + TICK_LAG} />
              <p className="text-3xl leading-relaxed text-white/90">{item.text}</p>
            </GlassCard>
          </RevealItem>
        ))}
      </RevealList>
    </SlideShell>
  );
}
